import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Tag,
  Space,
  Typography,
  Alert,
  Spin,
  Empty,
  Row,
  Col,
  Badge,
  Divider,
  Collapse,
  InputNumber,
  Switch,
  Input,
} from "antd";
import {
  TruckOutlined,
  EuroCircleOutlined,
  InfoCircleOutlined,
  ShoppingOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { sendcloudService } from "../../services/sendcloudService";
import { supabase } from "../../lib/supabase";
import type { SendcloudShippingOption } from "../../types/sendcloud";
import { getCountryCode } from "../../utils/countryMapping";

// Helper function to parse and format Sendcloud API errors
const parseShippingError = (errorMessage: string): string => {
  try {
    // Check if it's a Sendcloud API error with specific validation messages
    if (errorMessage.includes("Sendcloud API error")) {
      // Extract the JSON error message from the error string
      const jsonMatch = errorMessage.match(/\{.*\}/);
      if (jsonMatch) {
        const errorJson = JSON.parse(jsonMatch[0]);

        if (errorJson.error) {
          const sendcloudError = errorJson.error;

          // Handle specific field validation errors
          if (sendcloudError.message) {
            // Parse field-specific errors like: 'postal_code: "Enter a valid zip code."'
            const fieldErrors =
              sendcloudError.message.match(/(\w+):\s*"([^"]+)"/g);
            if (fieldErrors) {
              const formattedErrors = fieldErrors.map((error: string) => {
                const [, field, message] =
                  error.match(/(\w+):\s*"([^"]+)"/) || [];
                return `${field?.replace("_", " ")}: ${message}`;
              });
              return `Sendcloud validation error: ${formattedErrors.join(
                ", "
              )}`;
            }

            // Return the raw message if we can't parse it better
            return `Sendcloud error: ${sendcloudError.message}`;
          }

          // Handle other error formats
          if (typeof sendcloudError === "string") {
            return `Sendcloud error: ${sendcloudError}`;
          }
        }
      }

      // If we can't parse the JSON, return a cleaned version
      return errorMessage.replace(
        /^.*Sendcloud API error[^:]*:\s*/,
        "Sendcloud error: "
      );
    }

    // For non-Sendcloud errors, return as-is
    return errorMessage;
  } catch {
    // If parsing fails, return the original error
    return errorMessage;
  }
};

const { Text } = Typography;

// Air freight constants for calculations
const AIR_FREIGHT = {
  VOLUMETRIC_FACTOR: 167, // 1 CBM = 167 kg for air freight
};

interface Product {
  id?: number;
  name?: string;
  gross_weight: number;
  cbm: number;
  value?: number;
  cartons?: number;
  pieces?: number;
}

interface DeliveryInfo {
  country?: string;
  postal_code?: string;
  city?: string;
  street_name?: string;
  house_number?: string;
  name?: string;
  company_name?: string;
  email?: string;
  telephone?: string;
}

interface CompanyInfo {
  name?: string;
  contact_person?: string;
  email?: string;
  phone_number?: string;
  user_companies?: Array<{
    user?: {
      email?: string;
    };
  }>;
}

interface BookingData {
  totalWeight: number;
  totalVolume: number;
  chargeableWeight: number;
  isAirFreight: boolean;
  products: Product[];
  delivery_info?: DeliveryInfo | DeliveryInfo[];
  company_info?: CompanyInfo | CompanyInfo[];
}

interface ShippingOptionsProps {
  deliveryInfo: {
    id?: number;
    country?: string;
    postal_code?: string;
    city?: string;
    street_name?: string;
    house_number?: string;
    instructions?: string;
  } | null;
  bookingId: number;
  // Override carton/pallet counts from container unloading process
  deliveryCartons?: number;
  deliveryPallets?: number;
  onCreateShipment?: (shippingMethodId: number) => void;
  onShipmentCreated?: () => void;
  disabled?: boolean;
}

export const ShippingOptions: React.FC<ShippingOptionsProps> = ({
  deliveryInfo,
  bookingId,
  deliveryCartons,
  deliveryPallets,
  onCreateShipment,
  onShipmentCreated,
  disabled = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [shippingOptions, setShippingOptions] = useState<
    SendcloudShippingOption[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [showRequestData, setShowRequestData] = useState(true);
  const [quantityInput, setQuantityInput] = useState<number | null>(1);
  const [requestData, setRequestData] = useState({
    // Address data (populated from booking/delivery info)
    address: {
      country: "" as string,
      postalCode: "" as string,
      city: "" as string,
      streetName: "" as string,
      houseNumber: "" as string,
      name: "" as string,
      companyName: "" as string,
      email: "" as string,
      telephone: "" as string,
    },
    // Package data (populated from booking products)
    package: {
      weight: 1000 as number, // in grams
      length: 30 as number, // in cm
      width: 20 as number, // in cm
      height: 10 as number, // in cm
      cartons: 1 as number | null,
      value: 0 as number, // insured value
    },
  });

  // Note: Country mapping is now handled by the centralized countryMapping utility

  // Populate request data from booking and delivery info
  const populateRequestData = () => {
    try {
      // Use the already loaded booking data
      const currentBookingData = bookingData;

      // Calculate package dimensions from booking data
      let packageWeight = 1000; // default 1kg
      let packageCartons = 1;
      let packageValue = 0;

      if (currentBookingData && currentBookingData.products.length > 0) {
        // Use delivery cartons from container unloading if available, otherwise calculate from products
        packageCartons =
          deliveryCartons ||
          currentBookingData.products.reduce(
            (sum: number, product: Product) => sum + (product.cartons || 1),
            0
          );

        // Calculate weight per carton (not total weight!)
        const totalWeightKg = currentBookingData.totalWeight;
        const weightPerCartonKg = totalWeightKg / Math.max(packageCartons, 1);
        packageWeight = Math.round(weightPerCartonKg * 1000); // Convert to grams

        // Always set insured value to 0
        packageValue = 0;
      }

      console.log(
        `Populating request data with weight per carton: ${packageWeight}g (${(
          packageWeight / 1000
        ).toFixed(2)}kg per carton) from ${
          currentBookingData?.products.length || 0
        } products, ${packageCartons} total cartons ${
          deliveryCartons
            ? "(from container unloading)"
            : "(from product estimates)"
        }`
      );

      // Calculate realistic dimensions from CBM
      const totalVolume = currentBookingData?.totalVolume || 0.006; // default 6 liters
      const estimatedLength = Math.min(
        Math.round(Math.cbrt(totalVolume * 1000000) * 1.5),
        200
      );
      const estimatedWidth = Math.min(
        Math.round(Math.cbrt(totalVolume * 1000000) * 1.2),
        120
      );
      const estimatedHeight = Math.min(
        Math.round(Math.cbrt(totalVolume * 1000000) * 0.8),
        100
      );

      // Get delivery address from booking data - these come as arrays from Supabase relations
      const deliveryData = Array.isArray(currentBookingData?.delivery_info)
        ? currentBookingData.delivery_info[0]
        : currentBookingData?.delivery_info;
      const companyData = Array.isArray(currentBookingData?.company_info)
        ? currentBookingData.company_info[0]
        : currentBookingData?.company_info;

      console.log("Delivery data (after array access):", deliveryData);
      console.log("Company data (after array access):", companyData);
      console.log("Raw booking data:", currentBookingData);

      // Get associated user emails from user_companies relationship
      const userCompanies = companyData?.user_companies || [];
      const associatedUserEmails = userCompanies
        .map((uc) => uc.user?.email)
        .filter((email): email is string =>
          Boolean(email && email.trim() !== "")
        );

      // Use first associated user email as fallback
      const associatedUserEmail =
        associatedUserEmails.length > 0 ? associatedUserEmails[0] : "";

      // Email fallback chain: delivery email > associated user email > company email
      const resolvedEmail =
        deliveryData?.email || associatedUserEmail || companyData?.email || "";

      console.log("Email resolution in frontend:", {
        deliveryEmail: deliveryData?.email || "none",
        associatedUserEmails: associatedUserEmails,
        selectedUserEmail: associatedUserEmail || "none",
        companyEmail: companyData?.email || "none",
        finalEmail: resolvedEmail,
      });

      setRequestData({
        address: {
          country: deliveryData?.country || deliveryInfo?.country || "",
          postalCode:
            deliveryData?.postal_code || deliveryInfo?.postal_code || "",
          city: deliveryData?.city || deliveryInfo?.city || "",
          streetName:
            deliveryData?.street_name || deliveryInfo?.street_name || "",
          houseNumber:
            deliveryData?.house_number || deliveryInfo?.house_number || "",
          name:
            deliveryData?.name ||
            companyData?.contact_person ||
            companyData?.name ||
            "",
          companyName: deliveryData?.company_name || companyData?.name || "",
          email: resolvedEmail,
          telephone: deliveryData?.telephone || companyData?.phone_number || "",
        },
        package: {
          weight: packageWeight,
          length: estimatedLength,
          width: estimatedWidth,
          height: estimatedHeight,
          cartons: packageCartons,
          value: packageValue,
        },
      });
    } catch (error) {
      console.error("Error populating request data:", error);
    }
  };

  const fetchBookingData = async () => {
    try {
      const { data: booking, error } = await supabase
        .from("bookings")
        .select(
          `
          *,
          booking_transport_types!transport_type(id, name, abbreviation),
          booking_product!booking_id(*),
          booking_delivery!booking_id(*),
          company!company_id(
            *,
            user_companies(
              user:safe_users(
                email
              )
            )
          )
        `
        )
        .eq("id", bookingId)
        .single();

      if (error || !booking) {
        console.error("Failed to fetch booking data:", error);
        return null;
      }

      // Get products array
      const products = Array.isArray(booking.booking_product)
        ? booking.booking_product
        : [];

      // Calculate totals
      const totalWeight = products.reduce(
        (sum: number, product: Product) => sum + (product.gross_weight || 0),
        0
      );
      const totalVolume = products.reduce(
        (sum: number, product: Product) => sum + (product.cbm || 0),
        0
      );

      // Check if air freight
      const transportTypes = Array.isArray(booking.booking_transport_types)
        ? booking.booking_transport_types
        : [booking.booking_transport_types].filter(Boolean);
      const isAirFreight = transportTypes[0]?.id === 1;

      // Calculate chargeable weight (for air freight, use higher of actual or volumetric)
      const volumetricWeight = totalVolume * AIR_FREIGHT.VOLUMETRIC_FACTOR;
      const chargeableWeight = isAirFreight
        ? Math.max(totalWeight, volumetricWeight)
        : totalWeight;

      const bookingData = {
        totalWeight,
        totalVolume,
        chargeableWeight,
        isAirFreight,
        products,
        delivery_info: Array.isArray(booking.booking_delivery)
          ? booking.booking_delivery
          : [booking.booking_delivery].filter(Boolean),
        company_info: Array.isArray(booking.company)
          ? booking.company
          : [booking.company].filter(Boolean),
      };

      setBookingData(bookingData);
      return bookingData;
    } catch (err) {
      console.error("Error fetching booking data:", err);
      return null;
    }
  };

  const fetchShippingOptions = async () => {
    if (!deliveryInfo?.country || !deliveryInfo?.postal_code) {
      setError("Delivery address is required to fetch shipping options");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use existing booking data if available, otherwise fetch it
      const currentBookingData = bookingData || (await fetchBookingData());

      const countryCode = getCountryCode(deliveryInfo.country);
      console.log(
        `Converting country "${deliveryInfo.country}" to ISO code "${countryCode}"`
      );

      // Always use 1kg (1000g) for fetching available options
      const weightInGrams = 1000; // Fixed 1kg for fetching options

      console.log(
        `Using default weight of ${weightInGrams}g (1kg) for fetching shipping options`
      );

      // Use request data for API call
      const finalCountry = requestData.address.country || countryCode;
      const finalPostalCode =
        requestData.address.postalCode || deliveryInfo.postal_code;

      const requestParams = {
        country: finalCountry,
        postalCode: finalPostalCode,
        weight: weightInGrams, // Always use 1kg for options
        // Pass full request data as custom API params for potential use in edge function
        customApiParams: {
          addressData: requestData.address,
          packageData: {
            ...requestData.package,
            weight: weightInGrams, // Override weight with 1kg for options
          },
        },
      };

      console.log(`Fetching shipping options with:`);
      console.log(`- Country: ${finalCountry}`);
      console.log(`- Postal Code: ${finalPostalCode}`);
      console.log(`- Weight: ${weightInGrams}g (fixed 1kg for options)`);
      console.log(
        `- Actual booking weight: ${
          currentBookingData?.totalWeight || "N/A"
        }kg across ${deliveryCartons || "N/A"} cartons`
      );
      console.log("Full request params:", requestParams);

      const response = await sendcloudService.getShippingOptions(requestParams);

      if (response.success && response.data) {
        const allShippingMethods = response.data.shipping_methods || [];

        // Filter shipping methods to only include those available for the target country
        const filteredMethods = allShippingMethods.filter(
          (method) =>
            method.countries &&
            method.countries.some(
              (country) =>
                country.iso_2.toLowerCase() === countryCode.toLowerCase()
            )
        );

        // For each method, keep only the pricing for the target country
        const targetCountryMethods = filteredMethods.map((method) => ({
          ...method,
          countries: method.countries.filter(
            (country) =>
              country.iso_2.toLowerCase() === countryCode.toLowerCase()
          ),
        }));

        // Filter to only show specific shipping methods
        const baseShippingMethodIds = [25013, 8229, 68, 16, 53]; // Transmission Euro Pallet, GLS EconomyParcel 0-32kg, Additional GLS method, Parcel/Boxes shipment, Pallet shipment

        // Add Belgium-specific shipping option 3366
        const allowedShippingMethodIds =
          countryCode === "BE"
            ? [...baseShippingMethodIds, 3366] // Belgium-specific shipping option
            : baseShippingMethodIds;

        const finalFilteredMethods = targetCountryMethods.filter((method) =>
          allowedShippingMethodIds.includes(method.id)
        );

        console.log(
          `Filtered ${allShippingMethods.length} methods to ${targetCountryMethods.length} for country ${countryCode}, then to ${finalFilteredMethods.length} allowed methods`
        );
        setShippingOptions(finalFilteredMethods);
      } else {
        // Parse and display better error messages
        const errorMessage = parseShippingError(
          response.error || "Failed to fetch shipping options"
        );
        setError(errorMessage);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Fetch booking data when component mounts or bookingId changes
  useEffect(() => {
    if (bookingId) {
      fetchBookingData();
    }
  }, [bookingId]);

  // Populate request data when booking data becomes available
  useEffect(() => {
    console.log("useEffect populateRequestData triggered with:", {
      deliveryInfo,
      bookingId,
      bookingData: !!bookingData,
      hasCountry: !!deliveryInfo?.country,
      hasPostalCode: !!deliveryInfo?.postal_code,
    });

    if (bookingData && deliveryInfo) {
      console.log("Calling populateRequestData...");
      populateRequestData();
    } else {
      console.log("Not calling populateRequestData - missing required data");
    }
  }, [bookingData, deliveryInfo]);

  // Fetch shipping options when delivery info changes or when booking data becomes available
  useEffect(() => {
    if (deliveryInfo?.country && deliveryInfo?.postal_code && bookingData) {
      fetchShippingOptions();
    }
  }, [deliveryInfo?.country, deliveryInfo?.postal_code, bookingId]);

  // Initial fetch when both delivery info and booking data are ready
  useEffect(() => {
    if (
      deliveryInfo?.country &&
      deliveryInfo?.postal_code &&
      bookingData &&
      shippingOptions.length === 0
    ) {
      fetchShippingOptions();
    }
  }, [bookingData]);

  const handleCreateShipment = async () => {
    if (!selectedOption || !onCreateShipment) return;

    try {
      setLoading(true);
      setError(null);

      // For pallet options (25013, 53), do not include dimensions
      const shouldIncludeDimensions =
        selectedOption !== 25013 && selectedOption !== 53;
      const isPalletShipment =
        selectedOption === 25013 || selectedOption === 53;

      // Calculate actual weight from booking data
      const currentBookingData = bookingData;
      let actualWeightInGrams = 1000; // Default fallback

      if (currentBookingData) {
        if (isPalletShipment) {
          // For pallet shipments, divide total weight by number of pallets
          const totalPallets = deliveryPallets || 1;
          const weightPerPalletKg =
            currentBookingData.totalWeight / totalPallets;
          actualWeightInGrams = Math.round(weightPerPalletKg * 1000);

          console.log(
            `Calculating pallet weight: ${currentBookingData.totalWeight}kg total ÷ ${totalPallets} pallets = ${weightPerPalletKg}kg per pallet (${actualWeightInGrams}g)`
          );
        } else {
          // For non-pallet shipments, calculate per carton as before
          const totalCartons =
            deliveryCartons ||
            currentBookingData.products.reduce(
              (sum: number, product: Product) => sum + (product.cartons || 1),
              0
            );

          // Calculate weight per carton
          const weightPerCartonKg =
            currentBookingData.totalWeight / Math.max(totalCartons, 1);
          actualWeightInGrams = Math.round(weightPerCartonKg * 1000);

          console.log(
            `Calculating carton weight: ${currentBookingData.totalWeight}kg total ÷ ${totalCartons} cartons = ${weightPerCartonKg}kg per carton (${actualWeightInGrams}g)`
          );
        }
      }

      // Use the actual weight for shipment creation
      const response = await sendcloudService.createShipment({
        bookingId: bookingId,
        shippingMethodId: selectedOption,
        parcelData: {
          weight: actualWeightInGrams,
          // Only include dimensions for non-pallet shipping methods
          ...(shouldIncludeDimensions && {
            length: requestData.package.length,
            width: requestData.package.width,
            height: requestData.package.height,
          }),
          cartons: getQuantityInfo(selectedOption)
            ? quantityInput ?? undefined
            : undefined,
        },
        addressOverrides: Object.keys(requestData.address).some(
          (key) => requestData.address[key as keyof typeof requestData.address]
        )
          ? requestData.address
          : undefined,
      });

      if (response.success) {
        // Call the parent callback with the created shipment data
        onCreateShipment(selectedOption);

        // Notify parent that shipment was created successfully
        onShipmentCreated?.();

        // Show success message or redirect
        console.log("Shipment created successfully:", response.data);
      } else {
        // Parse and display better error messages
        const errorMessage = parseShippingError(
          response.error || "Failed to create shipment"
        );
        setError(errorMessage);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  const formatWeight = (weight: string | number) => {
    const numWeight = typeof weight === "string" ? parseFloat(weight) : weight;
    if (numWeight < 1) {
      return `${(numWeight * 1000).toFixed(0)}g`;
    }
    return `${numWeight.toFixed(1)}kg`;
  };

  // Helper function to determine quantity type and default value
  const getCarrierColor = (carrier: string) => {
    const colors: Record<string, string> = {
      postnl: "orange",
      dhl: "red",
      dpd: "red",
      ups: "brown",
      fedex: "purple",
      gls: "blue",
      bpost: "yellow",
      chronopost: "red",
    };
    return colors[carrier.toLowerCase()] || "default";
  };

  const getQuantityInfo = (shippingOptionId: number | null) => {
    if (!shippingOptionId) return null;

    // Use delivery cartons/pallets from container unloading if available, otherwise fallback to product estimates
    const totalCartons =
      deliveryCartons ||
      bookingData?.products?.reduce(
        (sum, product) => sum + (product.cartons ?? 0),
        0
      ) ||
      1;

    const totalPallets = deliveryPallets || 1;

    if (shippingOptionId === 25013 || shippingOptionId === 53) {
      // Pallet shipping - use delivery pallets from container unloading
      return {
        type: "pallets",
        label: "Number of Pallets",
        defaultValue: totalPallets,
        shouldPrefill: true,
      };
    } else if (
      shippingOptionId === 8229 ||
      shippingOptionId === 3366 ||
      shippingOptionId === 68 ||
      shippingOptionId === 16
    ) {
      // Carton shipping - use delivery cartons from container unloading, but max 7
      // This applies to GLS EconomyParcel (8229), Belgium-specific option (3366), additional GLS method (68), and Parcel/Boxes shipment (16)
      return {
        type: "cartons",
        label: "Number of Cartons",
        defaultValue: Math.min(totalCartons, 7),
        shouldPrefill: true,
      };
    }

    return null;
  };

  // Handle option selection and set quantity
  const handleOptionSelection = (optionId: number) => {
    setSelectedOption(optionId);

    const quantityInfo = getQuantityInfo(optionId);
    if (quantityInfo) {
      setQuantityInput(
        quantityInfo.shouldPrefill ? quantityInfo.defaultValue : 1
      );
    } else {
      setQuantityInput(1);
    }
  };

  if (!deliveryInfo) {
    return (
      <Card>
        <Alert
          type="info"
          message="No Delivery Address"
          description="Please add a delivery address to view available shipping options."
          showIcon
          icon={<TruckOutlined />}
        />
      </Card>
    );
  }

  if (!deliveryInfo.country || !deliveryInfo.postal_code) {
    return (
      <Card>
        <Alert
          type="warning"
          message="Incomplete Address"
          description="Country and postal code are required to fetch shipping options."
          showIcon
          icon={<InfoCircleOutlined />}
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <TruckOutlined />
          <span>Available Shipping Options</span>
          <Badge count={shippingOptions.length} color="blue" />
        </Space>
      }
      extra={
        <Button
          onClick={fetchShippingOptions}
          loading={loading}
          disabled={disabled}
          size="small"
        >
          Refresh
        </Button>
      }
    >
      {/* Request Data Editor */}
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <FilterOutlined />
            <span>Request Data (Edit as needed)</span>
          </Space>
        }
        extra={
          <Switch
            checkedChildren="Show"
            unCheckedChildren="Hide"
            checked={showRequestData}
            onChange={setShowRequestData}
            size="small"
          />
        }
      >
        {showRequestData && (
          <Collapse
            ghost
            size="small"
            items={[
              {
                key: "address",
                label: "Delivery Address",
                children: (
                  <Row gutter={[16, 16]}>
                    {/* Address Form - Left Side */}
                    <Col span={16}>
                      <Row gutter={[12, 12]}>
                        <Col span={8}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Country:
                            </Typography.Text>
                            <Input
                              placeholder="Country"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.country}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    country: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={8}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Postal Code:
                            </Typography.Text>
                            <Input
                              placeholder="Postal code"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.postalCode}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    postalCode: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={8}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              City:
                            </Typography.Text>
                            <Input
                              placeholder="City"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.city}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    city: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Street Name:
                            </Typography.Text>
                            <Input
                              placeholder="Street name"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.streetName}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    streetName: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              House Number:
                            </Typography.Text>
                            <Input
                              placeholder="House number"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.houseNumber}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    houseNumber: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Contact Name:
                            </Typography.Text>
                            <Input
                              placeholder="Contact name"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.name}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    name: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Company Name:
                            </Typography.Text>
                            <Input
                              placeholder="Company name"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.companyName}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    companyName: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Email:
                            </Typography.Text>
                            <Input
                              placeholder="Email"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.email}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    email: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Telephone:
                            </Typography.Text>
                            <Input
                              placeholder="Telephone"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.telephone}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    telephone: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                      </Row>
                    </Col>

                    {/* Booking Products - Right Side */}
                    <Col span={8}>
                      <div
                        style={{
                          border: "1px solid #f0f0f0",
                          borderRadius: "6px",
                          padding: "12px",
                          backgroundColor: "#fafafa",
                          height: "fit-content",
                        }}
                      >
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Booking Products ({bookingData?.products?.length || 0}
                          )
                        </Typography.Text>
                        <div
                          style={{
                            marginTop: 8,
                            maxHeight: "200px",
                            overflowY: "auto",
                          }}
                        >
                          {bookingData?.products &&
                          bookingData.products.length > 0 ? (
                            bookingData.products.map((product, index) => (
                              <div
                                key={product.id || index}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "6px 8px",
                                  marginBottom: "4px",
                                  backgroundColor: "white",
                                  borderRadius: "4px",
                                  border: "1px solid #e8e8e8",
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <Typography.Text
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 500,
                                      display: "block",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {product.name || `Product ${index + 1}`}
                                  </Typography.Text>
                                  <Typography.Text
                                    type="secondary"
                                    style={{ fontSize: 10 }}
                                  >
                                    {product.gross_weight
                                      ? `${product.gross_weight}kg`
                                      : "No weight"}
                                  </Typography.Text>
                                </div>
                                <div style={{ marginLeft: 8 }}>
                                  <Tag
                                    color="blue"
                                    style={{ fontSize: 10, margin: 0 }}
                                  >
                                    {product.cartons || 1} carton
                                    {(product.cartons || 1) !== 1 ? "s" : ""}
                                  </Tag>
                                </div>
                              </div>
                            ))
                          ) : (
                            <Typography.Text
                              type="secondary"
                              style={{ fontSize: 11, fontStyle: "italic" }}
                            >
                              No products found
                            </Typography.Text>
                          )}

                          {/* Total Summary */}
                          {bookingData?.products &&
                            bookingData.products.length > 0 && (
                              <div
                                style={{
                                  marginTop: 8,
                                  paddingTop: 8,
                                  borderTop: "1px solid #e8e8e8",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography.Text
                                  strong
                                  style={{ fontSize: 11 }}
                                >
                                  Total:
                                </Typography.Text>
                                <Tag
                                  color={deliveryCartons ? "green" : "orange"}
                                  style={{ fontSize: 10, margin: 0 }}
                                >
                                  {deliveryCartons ||
                                    bookingData.products.reduce(
                                      (sum, product) =>
                                        sum + (product.cartons || 1),
                                      0
                                    )}{" "}
                                  cartons
                                  {deliveryCartons
                                    ? " (actual from unloading)"
                                    : " (estimated)"}
                                </Tag>
                              </div>
                            )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                ),
              },
              {
                key: "package",
                label: "Package Details",
                children: (
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <div>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Weight (grams):
                        </Typography.Text>
                        <InputNumber
                          placeholder="Weight in grams"
                          style={{ width: "100%", marginTop: 4 }}
                          size="small"
                          min={1}
                          max={999999000}
                          value={requestData.package.weight}
                          onChange={(value) =>
                            setRequestData((prev) => ({
                              ...prev,
                              package: {
                                ...prev.package,
                                weight: value || 1000,
                              },
                            }))
                          }
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Length (cm):
                        </Typography.Text>
                        <InputNumber
                          placeholder="Length in cm"
                          style={{ width: "100%", marginTop: 4 }}
                          size="small"
                          min={1}
                          max={200}
                          value={requestData.package.length}
                          onChange={(value) =>
                            setRequestData((prev) => ({
                              ...prev,
                              package: {
                                ...prev.package,
                                length: value || 30,
                              },
                            }))
                          }
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Width (cm):
                        </Typography.Text>
                        <InputNumber
                          placeholder="Width in cm"
                          style={{ width: "100%", marginTop: 4 }}
                          size="small"
                          min={1}
                          max={120}
                          value={requestData.package.width}
                          onChange={(value) =>
                            setRequestData((prev) => ({
                              ...prev,
                              package: {
                                ...prev.package,
                                width: value || 20,
                              },
                            }))
                          }
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Height (cm):
                        </Typography.Text>
                        <InputNumber
                          placeholder="Height in cm"
                          style={{ width: "100%", marginTop: 4 }}
                          size="small"
                          min={1}
                          max={100}
                          value={requestData.package.height}
                          onChange={(value) =>
                            setRequestData((prev) => ({
                              ...prev,
                              package: {
                                ...prev.package,
                                height: value || 10,
                              },
                            }))
                          }
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Cartons:
                        </Typography.Text>
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#8c8c8c",
                            marginBottom: 2,
                          }}
                        >
                          {deliveryCartons
                            ? "(actual from container unloading)"
                            : "(sum of all products)"}
                        </div>
                        <InputNumber
                          placeholder="Number of cartons"
                          style={{ width: "100%", marginTop: 4 }}
                          size="small"
                          min={1}
                          max={100}
                          value={requestData.package.cartons}
                          onChange={(value) =>
                            setRequestData((prev) => ({
                              ...prev,
                              package: {
                                ...prev.package,
                                cartons: value,
                              },
                            }))
                          }
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Insured Value (€):
                        </Typography.Text>
                        <InputNumber
                          placeholder="Insured value"
                          style={{ width: "100%", marginTop: 4 }}
                          size="small"
                          min={0}
                          step={0.01}
                          precision={2}
                          value={requestData.package.value}
                          onChange={(value) =>
                            setRequestData((prev) => ({
                              ...prev,
                              package: {
                                ...prev.package,
                                value: value || 0,
                              },
                            }))
                          }
                        />
                      </div>
                    </Col>
                  </Row>
                ),
              },
            ]}
          />
        )}
      </Card>

      {loading && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Fetching available shipping options...</Text>
          </div>
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message={
            error.includes("validation error")
              ? "Address Validation Error"
              : error.includes("create shipment")
              ? "Shipment Creation Failed"
              : "Shipping Service Error"
          }
          description={
            <div>
              <div style={{ marginBottom: 8 }}>{error}</div>
              {error.toLowerCase().includes("postal") && (
                <div style={{ fontSize: 12, color: "#666" }}>
                  💡 <strong>Tip:</strong> Please check that the postal code
                  format is correct for{" "}
                  {deliveryInfo?.country || "the destination country"}.
                </div>
              )}
              {error.toLowerCase().includes("address") && (
                <div style={{ fontSize: 12, color: "#666" }}>
                  💡 <strong>Tip:</strong> Please verify all address fields are
                  filled in correctly.
                </div>
              )}
            </div>
          }
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
          action={
            <Button
              size="small"
              onClick={() => {
                setError(null);
                fetchShippingOptions();
              }}
            >
              Retry
            </Button>
          }
        />
      )}

      {!loading && !error && shippingOptions.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No shipping options available for this destination"
        />
      )}

      {!loading && !error && shippingOptions.length > 0 && (
        <>
          {(() => {
            const sortedOptions = [...shippingOptions].sort((a, b) => {
              const priceA =
                a.countries && a.countries[0] ? a.countries[0].price : Infinity;
              const priceB =
                b.countries && b.countries[0] ? b.countries[0].price : Infinity;
              return priceA - priceB;
            });

            const renderOption = (option: SendcloudShippingOption) => (
              <Col span={8} key={option.id} style={{ marginBottom: 16 }}>
                <Card
                  size="small"
                  style={{
                    cursor: "pointer",
                    border:
                      selectedOption === option.id
                        ? "2px solid #1890ff"
                        : "1px solid #f0f0f0",
                    borderRadius: 8,
                    backgroundColor:
                      selectedOption === option.id ? "#f6ffed" : "white",
                    height: "100%",
                  }}
                  bodyStyle={{ padding: 12 }}
                  onClick={() => handleOptionSelection(option.id)}
                >
                  <div style={{ textAlign: "center", marginBottom: 8 }}>
                    <TruckOutlined style={{ fontSize: 20, color: "#1890ff" }} />
                    <br />
                    <div style={{ margin: "4px 0 0 0" }}>
                      <img
                        src={`https://cdn.sendcloud.com/global-media/${option.carrier.toLowerCase()}/img/logo.svg`}
                        alt={option.carrier}
                        style={{
                          height: "16px",
                          maxWidth: "40px",
                          objectFit: "contain",
                          marginBottom: 4,
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      <br />
                      <Tag
                        color={getCarrierColor(option.carrier)}
                        style={{ fontSize: "10px" }}
                      >
                        {option.carrier.toUpperCase()}
                      </Tag>
                    </div>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                      }}
                    >
                      <Text strong style={{ fontSize: 14 }}>
                        {option.name}
                      </Text>
                      <Space>
                        {option.countries &&
                          option.countries.length > 0 &&
                          option.countries[0].price > 0 && (
                            <Tag color="green" icon={<EuroCircleOutlined />}>
                              €{option.countries[0].price.toFixed(2)}
                            </Tag>
                          )}
                      </Space>
                    </div>
                  </div>

                  <Row gutter={[8, 8]}>
                    <Col span={12}>
                      <div>
                        <Text type="secondary" style={{ fontSize: "11px" }}>
                          Weight Range:
                        </Text>
                        <br />
                        <Text style={{ fontSize: "11px" }}>
                          {formatWeight(option.min_weight)} -{" "}
                          {formatWeight(option.max_weight)}
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div>
                        <Text type="secondary" style={{ fontSize: "11px" }}>
                          Delivery:
                        </Text>
                        <br />
                        <Text style={{ fontSize: "11px" }}>
                          {option.countries &&
                          option.countries[0]?.lead_time_hours
                            ? `${option.countries[0].lead_time_hours}h`
                            : "N/A"}
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Tag
                        color={
                          option.service_point_input === "required"
                            ? "orange"
                            : option.service_point_input === "optional"
                            ? "blue"
                            : "green"
                        }
                        style={{ fontSize: "10px" }}
                      >
                        {option.service_point_input === "required"
                          ? "Service Point"
                          : option.service_point_input === "optional"
                          ? "Optional Point"
                          : "Home Delivery"}
                      </Tag>
                    </Col>
                  </Row>
                </Card>
              </Col>
            );

            return (
              <>
                {/* Show all options in 3-column grid */}
                <Row gutter={[16, 16]}>{sortedOptions.map(renderOption)}</Row>
              </>
            );
          })()}

          {selectedOption && (
            <>
              <Divider />

              {/* Quantity Input for specific shipping options */}
              {getQuantityInfo(selectedOption) && (
                <div style={{ marginBottom: 16, textAlign: "center" }}>
                  <Space direction="vertical" align="center">
                    <Text strong>{getQuantityInfo(selectedOption)?.label}</Text>
                    <InputNumber
                      min={1}
                      max={
                        getQuantityInfo(selectedOption)?.type === "cartons"
                          ? 7
                          : 50
                      }
                      value={quantityInput}
                      onChange={(value) => setQuantityInput(value)}
                      style={{
                        width: 120,
                        borderColor:
                          getQuantityInfo(selectedOption)?.type === "cartons" &&
                          quantityInput &&
                          quantityInput > 7
                            ? "#ff4d4f"
                            : undefined,
                      }}
                    />
                    {getQuantityInfo(selectedOption)?.type === "cartons" &&
                      quantityInput &&
                      quantityInput > 7 && (
                        <Text
                          type="danger"
                          style={{ fontSize: "11px", marginTop: "4px" }}
                        >
                          Maximum 7 cartons allowed
                        </Text>
                      )}
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      This will create {quantityInput || 0} separate labels in
                      Sendcloud
                    </Text>
                    {getQuantityInfo(selectedOption)?.type === "cartons" && (
                      <Text
                        type="secondary"
                        style={{ fontSize: "11px", fontStyle: "italic" }}
                      >
                        ℹ️{" "}
                        {deliveryCartons
                          ? "Actual cartons from container unloading"
                          : "Sum of all product cartons from booking"}
                      </Text>
                    )}
                    {(selectedOption === 25013 || selectedOption === 53) && (
                      <Text
                        type="secondary"
                        style={{ fontSize: "11px", fontStyle: "italic" }}
                      >
                        ℹ️{" "}
                        {deliveryPallets
                          ? "Actual pallets from container unloading"
                          : "Multicollo support: This shipping method can be used for multiple cartons/pallets."}
                      </Text>
                    )}
                  </Space>
                </div>
              )}

              <div style={{ textAlign: "center" }}>
                <Space>
                  <Button
                    type="primary"
                    size="large"
                    icon={<ShoppingOutlined />}
                    onClick={handleCreateShipment}
                    disabled={
                      disabled ||
                      Boolean(
                        getQuantityInfo(selectedOption) &&
                          quantityInput === null
                      ) ||
                      Boolean(
                        getQuantityInfo(selectedOption)?.type === "cartons" &&
                          quantityInput &&
                          quantityInput > 7
                      )
                    }
                    loading={loading}
                  >
                    Create Shipment with Selected Option
                  </Button>
                  <Button
                    onClick={() => setSelectedOption(null)}
                    disabled={disabled}
                  >
                    Clear Selection
                  </Button>
                </Space>
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
};
